import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useViewAs } from "@/hooks/useViewAs";

// The Alpha with AI Club is kept for the people already in it and closed to new sign-ups (his ruling,
// 30 Oct 2026, option C of output/site-pages/SN_ONE_LOGIN_TWO_PRODUCTS_PLAN_2026-10-30.md in the vault).
// A Club member is anyone with an account made before this moment, or with any Club payment, Founding
// or complimentary row (all of which live in `subscriptions` / `founding_subscriptions`), or an admin.
// Accounts made after it are Data accounts. This only decides what the menus and the account page show;
// the Club's paywall (has_active_subscription) and the Data plans are untouched.
export const CLUB_CUTOFF = "2026-10-31T00:00:00+05:30";

// one lookup per account per page load, shared by every caller (menus, cards, gates)
const lookups = new Map<string, Promise<boolean>>();
const lookup = (uid: string) => {
  if (!lookups.has(uid)) {
    lookups.set(uid, Promise.all([
      supabase.from("subscriptions").select("id").eq("user_id", uid).limit(1),
      supabase.from("founding_subscriptions").select("id").eq("user_id", uid).limit(1),
    ]).then(([s, f]) => {
      // a failed read counts as a member, so a lookup error never hides the Club from someone inside it;
      // it is not kept, so the next page load asks again
      if (s.error || f.error) lookups.delete(uid);
      return !!s.error || !!f.error || !!s.data?.length || !!f.data?.length;
    }, () => { lookups.delete(uid); return true; }));
  }
  return lookups.get(uid)!;
};

export function useClubMember(user: User | null) {
  const { isRealAdmin } = useAuth();
  const { role } = useViewAs();
  const [paid, setPaid] = useState<boolean | null>(null);

  const early = !!user && new Date(user.created_at).getTime() < new Date(CLUB_CUTOFF).getTime();
  const known = !user || early || isRealAdmin || role !== "auto";
  const uid = user?.id;

  useEffect(() => {
    setPaid(null);
    if (known) return;
    let active = true;
    lookup(uid!).then((v) => { if (active) setPaid(v); });
    return () => { active = false; };
  }, [known, uid]);

  if (!user) return { isClubMember: false, isLoading: false };
  if (known) return { isClubMember: true, isLoading: false };
  return { isClubMember: !!paid, isLoading: paid === null };
}
